import classNames from 'classnames';
import { useMemo, useCallback, ReactElement, ReactNode } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SORTING_DIRECTION } from 'src/constants/main';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';

interface IProps<T> {
  sortField?: T;
  currentField?: T | null;
  currentDir?: SORTING_DIRECTION | null;
  changeSorting?: (sortField: T | undefined, sortDir: SORTING_DIRECTION) => void;
  className?: string;
  buttonsClassName?: string;
  children?: ReactNode;
}

const SortingButton = <T,>({ sortField, currentField, currentDir, changeSorting, ...props }: IProps<T>): ReactElement => {
  const isActive: boolean = useMemo((): boolean => currentField === sortField, [currentField, sortField]);
  const isAsc: boolean = useMemo((): boolean => currentDir === SORTING_DIRECTION.ASCENDING, [currentDir]);

  const handleClick = useCallback(() => {
    if (typeof changeSorting !== 'function') return;

    if (isActive && isAsc) {
      changeSorting(sortField, SORTING_DIRECTION.DESCENDING);
    } else {
      changeSorting(sortField, SORTING_DIRECTION.ASCENDING);
    }
  }, [isActive, isAsc, sortField]);

  const ascIconClassNames = useMemo((): string => {
    return classNames(`sorting-button__arrow-asc`, { 'sorting-button__arrow-asc_active': isActive && isAsc });
  }, [isActive, isAsc]);

  const descIconClassNames = useMemo((): string => {
    return classNames(`sorting-button__arrow-desc`, { 'sorting-button__arrow-desc_active': isActive && !isAsc });
  }, [isActive, isAsc]);

  return (
    <button className={classNames('sorting-button', props.className)} onClick={handleClick}>
      {props.children}
      <div className={classNames('sorting-button__arrow', props.buttonsClassName)}>
        <Icon size={12} name={Icons.ArrowHalf} className={ascIconClassNames} />
        <Icon size={12} name={Icons.ArrowHalf} className={descIconClassNames} />
      </div>
    </button>
  );
};

export default SortingButton;
